/**
 * Client caller for the `threadMembershipRead` Cloud Function (group-threading P1b).
 *
 * Firestore rules deny client reads of `prest0n_thread_participants` (server-owned
 * authorization state), so the browser cannot read membership directly — it goes
 * through this server-mediated endpoint, which returns ONLY the threads the caller
 * participates in. Fail-closed by contract: any error / no-auth resolves to `[]`
 * (never throws, never surfaces another user's membership).
 */
import { getAuth } from 'firebase/auth';
import {
  HISTORY_FLOOR_FAIL_CLOSED,
  setGroupThreadHistoryFloors,
} from '../services/groupThreads/historyFloors';
import { normalizeCanonicalThreadTitle } from '../services/groupThreads/canonicalThreadTitle';

export interface MembershipReadThread {
  threadId: string;
  workspaceId: string;
  /** Real group members (never `[self]`). */
  participantIds: string[];
  createdBy?: string;
  createdAt?: number;
  /**
   * The CALLER'S OWN history floor for this thread (GT-01): the lowest
   * `orderTs` they may read. 0 = full history. This endpoint is the only way a
   * browser can learn it — rules deny client reads of the participant registry
   * outright. Fail-closed to MAX_SAFE_INTEGER when the server omits it or sends
   * anything that is not a non-negative integer.
   */
  historyFloor: number;
  /** Canonical owner title for recipient chips/panes. */
  title?: string;
  /**
   * uid → display name, resolved SERVER-side against the thread's own
   * workspace (2026-08-26 sender-name work). Fills the roster gap for
   * cross-workspace members and roster-read misses; see MembershipReadThread
   * on the server for the full reasoning.
   */
  participantNames?: Record<string, string>;
  /**
   * Per-thread "@Prest0n mention required to summon" setting. Present only when
   * a thread has explicitly RELAXED it; absent ⇒ required, which is the stated
   * default for new and existing threads alike. Read by the summon gate
   * (src/services/threadMentions/summonGate.ts), which additionally never
   * suppresses in a one-on-one chat.
   */
  requireMentionToSummon?: boolean;
}

/** Resolve the `threadMembershipRead` endpoint (env override → prod cloudfunctions URL). */
export function threadMembershipReadUrl(): string {
  const override = (process.env.REACT_APP_FUNCTIONS_BASE || '').trim();
  const base = override
    ? override.replace(/\/$/, '')
    : `https://us-central1-${
        process.env.REACT_APP_FIREBASE_PROJECT_ID || 'tensile-tenure-453409-k3'
      }.cloudfunctions.net`;
  return `${base}/threadMembershipRead`;
}

/** Normalize a raw CF row into a `MembershipReadThread`, dropping malformed entries. */
export function normalizeMembershipRows(raw: unknown): MembershipReadThread[] {
  const rows = Array.isArray(raw) ? raw : [];
  const out: MembershipReadThread[] = [];
  for (const r of rows as any[]) {
    const threadId = typeof r?.threadId === 'string' ? r.threadId.trim() : '';
    if (!threadId) continue;
    const participantIds = Array.isArray(r?.participantIds)
      ? r.participantIds.map((id: unknown) => String(id ?? '').trim()).filter(Boolean)
      : [];
    const workspaceId = typeof r?.workspaceId === 'string' ? r.workspaceId.trim() : '';
    const createdBy = typeof r?.createdBy === 'string' ? r.createdBy.trim() || undefined : undefined;
    out.push({
      threadId,
      workspaceId,
      participantIds,
      ...((): { participantNames?: Record<string, string> } => {
        const raw = (r as { participantNames?: unknown })?.participantNames;
        if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
        const names: Record<string, string> = {};
        for (const [uid, name] of Object.entries(raw as Record<string, unknown>)) {
          const key = String(uid ?? '').trim();
          const value = typeof name === 'string' ? name.trim() : '';
          if (key && value) names[key] = value.slice(0, 120);
        }
        return Object.keys(names).length ? { participantNames: names } : {};
      })(),
      createdBy,
      createdAt: Number.isFinite(r?.createdAt) ? Number(r.createdAt) : undefined,
      // Fail closed on anything the rules would reject, including a response
      // from a server build that predates GT-01 and omits the field entirely.
      historyFloor:
        typeof r?.historyFloor === 'number' && Number.isInteger(r.historyFloor) && r.historyFloor >= 0
          ? r.historyFloor
          : HISTORY_FLOOR_FAIL_CLOSED,
      title: normalizeCanonicalThreadTitle(
        r?.title,
        [threadId, workspaceId, createdBy, ...participantIds],
      ),
      // Only an explicit false relaxes the requirement. Anything else — absent,
      // null, a string from an older server build — reads as required.
      ...(r?.requireMentionToSummon === false ? { requireMentionToSummon: false } : {}),
    });
  }
  return out;
}

export interface ThreadMembershipReadResult {
  /**
   * `true` ⇔ the server actually answered with a membership list (possibly
   * empty). `false` ⇔ TRANSPORT failure (no token, non-2xx, network error,
   * malformed body) — the read said nothing about the caller's real
   * membership, and `rows` is always `[]` then.
   */
  ok: boolean;
  rows: MembershipReadThread[];
}

/**
 * Fetch the signed-in caller's group-thread membership, distinguishing a
 * transport failure from a genuine "zero group threads" answer. The two used
 * to be indistinguishable (both `[]`), so one failed fetch collapsed the
 * sidebar's "group threads" section to the flat layout and desynced tabs of
 * the same account (2026-08-08 parity report). Never throws; never surfaces
 * another user's membership. A signed-out caller is a genuine empty
 * (`ok:true`) — there is no membership to read.
 */
export async function fetchThreadMembershipResult(
  signal?: AbortSignal,
): Promise<ThreadMembershipReadResult> {
  try {
    const user = getAuth().currentUser;
    if (!user) return { ok: true, rows: [] };
    const token = await user.getIdToken();
    if (!token) return { ok: false, rows: [] };
    const res = await fetch(threadMembershipReadUrl(), {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
      signal,
    });
    if (!res.ok) return { ok: false, rows: [] };
    const body = await res.json().catch(() => null);
    // The endpoint's success shape is `{ threads: [...] }` — anything else is
    // a malformed answer, not an empty membership.
    if (!body || !Array.isArray((body as { threads?: unknown }).threads)) {
      return { ok: false, rows: [] };
    }
    const rows = normalizeMembershipRows(body.threads);
    // Publish the caller's floors for the repository-layer overlay query, which
    // has no membership in scope. This fetch is the only place they arrive.
    setGroupThreadHistoryFloors(rows);
    return { ok: true, rows };
  } catch {
    return { ok: false, rows: [] };
  }
}

/**
 * Fetch the signed-in caller's group-thread membership. Fail-closed → `[]` on
 * no-auth, non-2xx, network error, or malformed body.
 */
export async function fetchThreadMembership(signal?: AbortSignal): Promise<MembershipReadThread[]> {
  return (await fetchThreadMembershipResult(signal)).rows;
}

/**
 * Strict variant for consumers that must NOT treat a transport failure as an
 * empty membership (e.g. the sidebar's participant-count loader, which would
 * otherwise wipe a populated "group threads" section): THROWS on transport
 * failure, resolves rows (possibly `[]`) only when the server really answered.
 */
export async function fetchThreadMembershipStrict(
  signal?: AbortSignal,
): Promise<MembershipReadThread[]> {
  const { ok, rows } = await fetchThreadMembershipResult(signal);
  if (!ok) throw new Error('threadMembershipRead transport failure');
  return rows;
}
